import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { facturacionAPI, notasCreditoAPI } from '../services/api';
import { useApp } from '../context/AppContext';

const FacturacionElectronica = () => {
  const { formatCurrency } = useApp();
  const [activeTab, setActiveTab] = useState('comprobantes');
  const [comprobantes, setComprobantes] = useState([]);
  const [notasCredito, setNotasCredito] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [paginationNC, setPaginationNC] = useState({ page: 1, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ estado: '', tipo: '', buscar: '', fechaDesde: '', fechaHasta: '' });
  const [showDetalle, setShowDetalle] = useState(null);
  const [procesando, setProcesando] = useState({});
  const [certificadoInfo, setCertificadoInfo] = useState(null);

  // NC Modal state
  const [showNCModal, setShowNCModal] = useState(false);
  const [ncMotivo, setNcMotivo] = useState('');
  const [ncComprobante, setNcComprobante] = useState(null);
  const [ncGenerando, setNcGenerando] = useState(false);

  // Devolucion Modal state
  const [showDevModal, setShowDevModal] = useState(false);
  const [devNC, setDevNC] = useState(null);
  const [devoluciones, setDevoluciones] = useState([]);
  const [devLoading, setDevLoading] = useState(false);
  const [devProcesando, setDevProcesando] = useState({});
  const [devReferencias, setDevReferencias] = useState({});

  // Cargar comprobantes
  const cargarComprobantes = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20, ...filters };
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await facturacionAPI.getAll(params);
      // FIX: null checks on API response data
      if (res.data?.success) {
        setComprobantes(res.data.data || []);
        setPagination(res.data.pagination || { page, total: (res.data.data || []).length, totalPages: 1 });
      }
    } catch (err) {
      console.error('Error al cargar comprobantes:', err);
      toast.error(err.response?.data?.error || 'Error al cargar comprobantes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Cargar notas de credito
  const cargarNotasCredito = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      const res = await notasCreditoAPI.getAll(params);
      // FIX: null checks on API response data
      if (res.data?.success) {
        const ncData = res.data.data || [];
        setNotasCredito(ncData);
        setPaginationNC(res.data.pagination || { page: res.data.page || page, total: res.data.total || ncData.length, totalPages: res.data.totalPages || 1 });
      }
    } catch (err) {
      console.error('Error al cargar notas de credito:', err);
      toast.error(err.response?.data?.error || 'Error al cargar notas de credito');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar info certificado
  const cargarCertificado = useCallback(async () => {
    try {
      const res = await facturacionAPI.getCertificadoInfo();
      // FIX: null check on certificado response
      if (res.data?.success) setCertificadoInfo(res.data.data || null);
      else setCertificadoInfo(null);
    } catch { setCertificadoInfo(null); }
  }, []);

  useEffect(() => {
    if (activeTab === 'comprobantes') cargarComprobantes();
    else cargarNotasCredito();
    cargarCertificado();
  }, [activeTab, cargarComprobantes, cargarNotasCredito, cargarCertificado]);

  // Acciones sobre comprobante
  const ejecutarAccion = async (id, accion, label) => {
    setProcesando(prev => ({ ...prev, [id]: accion }));
    try {
      let res;
      switch (accion) {
        case 'enviar':
          res = await facturacionAPI.enviar(id);
          break;
        case 'consultar':
          res = await facturacionAPI.consultar(id);
          break;
        default:
          return;
      }
      // FIX: null checks on accion response and detail reload
      if (res?.data?.success) {
        toast.success(`${label} exitoso`);
        if (activeTab === 'comprobantes') {
          cargarComprobantes(pagination.page);
        } else {
          cargarNotasCredito(paginationNC.page);
        }
        if (showDetalle?.id === id) {
          const det = await facturacionAPI.getById(id);
          if (det.data?.success) setShowDetalle(det.data.data);
        }
      } else {
        toast.error(res?.data?.error || `Error en ${label}`);
      }
    } catch (err) {
      console.error(`Error en ${label}:`, err);
      toast.error(err.response?.data?.error || `Error al ${label.toLowerCase()}`);
    } finally {
      setProcesando(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  // Descargar RIDE PDF
  const descargarRIDE = async (id) => {
    try {
      const res = await facturacionAPI.descargarRIDE(id);
      // FIX: validate response data before creating blob
      if (!res.data) { toast.error('No se recibio contenido del RIDE'); return; }
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RIDE_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar RIDE:', err);
      toast.error(err.response?.data?.error || 'Error al descargar RIDE');
    }
  };

  // Enviar por email
  const enviarEmail = async (id) => {
    setProcesando(prev => ({ ...prev, [id]: 'email' }));
    try {
      const res = await facturacionAPI.reenviarEmail(id);
      // FIX: null check on email response
      if (res.data?.success) {
        toast.success(res.data.message || 'Email enviado correctamente');
      } else {
        toast.error(res.data?.message || 'Error al enviar email');
      }
    } catch (err) {
      console.error('Error al enviar email:', err);
      toast.error(err.response?.data?.error || 'Error al enviar email');
    } finally {
      setProcesando(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  // Descargar XML
  const descargarXML = async (id) => {
    try {
      const res = await facturacionAPI.descargarXML(id);
      // FIX: validate response data before creating blob
      if (!res.data) { toast.error('No se recibio contenido del XML'); return; }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comprobante_${id}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error al descargar XML:', err);
      toast.error(err.response?.data?.error || 'Error al descargar XML');
    }
  };

  // Ver detalle
  const verDetalle = async (id) => {
    try {
      const res = await facturacionAPI.getById(id);
      // FIX: null check on detalle response
      if (res.data?.success && res.data.data) {
        setShowDetalle(res.data.data);
      } else {
        toast.error('No se encontro el detalle del comprobante');
      }
    } catch (err) {
      console.error('Error al cargar detalle:', err);
      toast.error(err.response?.data?.error || 'Error al cargar detalle');
    }
  };

  // ═══ NOTA DE CREDITO ═══
  const abrirNCModal = (comprobante) => {
    setNcComprobante(comprobante);
    setNcMotivo('');
    setShowNCModal(true);
  };

  const generarNotaCredito = async () => {
    if (!ncMotivo.trim()) {
      toast.warning('Debe ingresar el motivo de la anulacion');
      return;
    }
    if (!ncComprobante?.venta_id) {
      toast.error('Este comprobante no tiene venta asociada');
      return;
    }

    setNcGenerando(true);
    try {
      const res = await notasCreditoAPI.generar(ncComprobante.venta_id, { motivo: ncMotivo.trim() });
      // FIX: null check on nota credito generation response
      if (res.data?.success) {
        const nc = res.data.data || {};
        const estadoNC = nc.estado || 'generado';
        if (estadoNC === 'autorizado') {
          toast.success('Nota de Credito generada y AUTORIZADA por el SRI');
        } else if (estadoNC === 'recibido' || estadoNC === 'firmado') {
          toast.success(`Nota de Credito generada (estado: ${estadoNC.toUpperCase()}). Puede consultar la autorizacion desde la tab "Notas de Credito".`);
        } else {
          toast.success('Nota de Credito generada correctamente');
        }
        setShowNCModal(false);
        setNcMotivo('');
        setNcComprobante(null);
        // Recargar ambas tabs
        cargarComprobantes(pagination.page);
        cargarNotasCredito(1);
      } else {
        toast.error(res.data?.error || 'Error al generar nota de credito');
      }
    } catch (err) {
      console.error('Error al generar nota de credito:', err);
      const msg = err.response?.data?.error || 'Error al generar nota de credito';
      toast.error(msg);
    } finally {
      setNcGenerando(false);
    }
  };

  // ═══ DEVOLUCIONES ═══
  const abrirDevolucionModal = async (nc) => {
    setDevNC(nc);
    setDevoluciones([]); // Limpiar datos anteriores
    setDevReferencias({}); // Limpiar referencias
    setShowDevModal(true);
    setDevLoading(true);
    try {
      const res = await notasCreditoAPI.getDevoluciones(nc.id);
      // FIX: null check on devoluciones data array
      if (res.data?.success) setDevoluciones(res.data.data || []);
    } catch (err) {
      console.error('Error al cargar devoluciones:', err);
      toast.error(err.response?.data?.error || 'Error al cargar devoluciones');
    } finally {
      setDevLoading(false);
    }
  };

  const completarDevolucion = async (devolucionId) => {
    setDevProcesando(prev => ({ ...prev, [devolucionId]: true }));
    try {
      const ref = devReferencias[devolucionId] || '';
      const res = await notasCreditoAPI.completarDevolucion(devolucionId, { referencia: ref });
      // FIX: null checks on devolucion completion response and reload
      if (res.data?.success) {
        toast.success('Devolucion marcada como completada');
        setDevReferencias(prev => { const n = { ...prev }; delete n[devolucionId]; return n; });
        // Recargar devoluciones
        if (devNC?.id) {
          const devRes = await notasCreditoAPI.getDevoluciones(devNC.id);
          if (devRes.data?.success) setDevoluciones(devRes.data.data || []);
        }
        cargarNotasCredito(paginationNC.page);
      } else {
        toast.error(res.data?.error || 'Error');
      }
    } catch (err) {
      console.error('Error al completar devolucion:', err);
      toast.error(err.response?.data?.error || 'Error al completar devolucion');
    } finally {
      setDevProcesando(prev => { const n = { ...prev }; delete n[devolucionId]; return n; });
    }
  };

  const metodoLabel = (m) => {
    const map = {
      efectivo: 'Efectivo', tarjeta: 'Tarjeta', tarjeta_medianet: 'Tarjeta Medianet',
      tarjeta_payphone: 'Tarjeta Payphone', transferencia: 'Transferencia',
      credito: 'Credito', tarjeta_credito: 'Tarjeta Credito', tarjeta_debito: 'Tarjeta Debito'
    };
    return map[m] || m;
  };

  // Badge estado
  const estadoBadge = (estado) => {
    const colors = {
      generado: 'secondary', firmado: 'info', enviado: 'primary',
      recibido: 'primary', autorizado: 'success', rechazado: 'danger',
      devuelto: 'warning', error: 'danger', anulado: 'dark'
    };
    return (
      <span className={`badge bg-${colors[estado] || 'secondary'}`}>
        {estado?.toUpperCase()}
      </span>
    );
  };

  // Tipo comprobante label
  const tipoLabel = (tipo) => {
    const tipos = { '01': 'Factura', '04': 'Nota de Credito', '05': 'Nota de Debito' };
    return tipos[tipo] || tipo;
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatFechaHora = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleString('es-EC', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Certificado Warning */}
      {!certificadoInfo && (
        <div className="alert alert-warning d-flex align-items-center mb-3">
          <i className="bi bi-exclamation-triangle-fill me-2 fs-5"></i>
          <div>
            <strong>Sin certificado digital.</strong> Suba un archivo .p12 en{' '}
            <strong>Configuracion &gt; SRI</strong> para poder firmar y enviar comprobantes.
          </div>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'comprobantes' ? 'active' : ''}`}
            onClick={() => setActiveTab('comprobantes')}
          >
            <i className="bi bi-file-earmark-check me-2"></i>Comprobantes
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'notas-credito' ? 'active' : ''}`}
            onClick={() => setActiveTab('notas-credito')}
          >
            <i className="bi bi-file-earmark-minus me-2"></i>Notas de Credito
          </button>
        </li>
      </ul>

      {/* Tab Comprobantes */}
      {activeTab === 'comprobantes' && (
        <div className="fade-in">
          {/* Filtros */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label small mb-1">Buscar</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Cedula, nombre, secuencial, codigo venta..."
                    value={filters.buscar}
                    onChange={(e) => setFilters({ ...filters, buscar: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && cargarComprobantes()}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Estado</label>
                  <select
                    className="form-select form-select-sm"
                    value={filters.estado}
                    onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
                  >
                    <option value="">Todos</option>
                    <option value="generado">Generado</option>
                    <option value="firmado">Firmado</option>
                    <option value="enviado">Enviado</option>
                    <option value="autorizado">Autorizado</option>
                    <option value="rechazado">Rechazado</option>
                    <option value="error">Error</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Desde</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.fechaDesde}
                    onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small mb-1">Hasta</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={filters.fechaHasta}
                    onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
                  />
                </div>
                <div className="col-md-3 d-flex gap-2">
                  <button className="btn btn-primary btn-sm" onClick={() => cargarComprobantes()}>
                    <i className="bi bi-search me-1"></i>Buscar
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => {
                    setFilters({ estado: '', tipo: '', buscar: '', fechaDesde: '', fechaHasta: '' });
                    setTimeout(() => cargarComprobantes(), 100);
                  }}>
                    <i className="bi bi-x-circle me-1"></i>Limpiar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla */}
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Secuencial</th>
                    <th>Receptor</th>
                    <th className="text-end">Total</th>
                    <th className="text-center">Estado</th>
                    <th>Autorizacion</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        <div className="spinner-border text-primary"></div>
                      </td>
                    </tr>
                  ) : comprobantes.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4 text-muted">
                        <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                        No hay comprobantes
                      </td>
                    </tr>
                  ) : comprobantes.map(c => (
                    <tr key={c.id}>
                      <td className="small">{formatFecha(c.created_at)}</td>
                      <td>
                        <span className={`badge ${c.tipo_comprobante === '01' ? 'bg-primary' : 'bg-info'} bg-opacity-10 ${c.tipo_comprobante === '01' ? 'text-primary' : 'text-info'}`}>
                          {tipoLabel(c.tipo_comprobante)}
                        </span>
                      </td>
                      <td className="font-monospace fw-bold">
                        {c.establecimiento}-{c.punto_emision}-{c.secuencial}
                      </td>
                      <td>
                        <div className="small fw-medium">{c.razon_social_receptor || 'CONSUMIDOR FINAL'}</div>
                        <div className="text-muted small">{c.identificacion_receptor || '9999999999999'}</div>
                      </td>
                      {/* FIX: null guard for importe_total in table */}
                      <td className="text-end fw-semibold">{formatCurrency(c.importe_total || 0)}</td>
                      <td className="text-center">{estadoBadge(c.estado)}</td>
                      <td className="small">
                        {c.numero_autorizacion ? (
                          <span className="font-monospace text-truncate d-inline-block" style={{ maxWidth: '140px' }} title={c.numero_autorizacion}>
                            {c.numero_autorizacion}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-secondary" onClick={() => verDetalle(c.id)} title="Ver detalle">
                            <i className="bi bi-eye"></i>
                          </button>
                          {c.estado === 'generado' && (
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => ejecutarAccion(c.id, 'enviar', 'Envio')}
                              disabled={!!procesando[c.id]}
                              title="Firmar y enviar al SRI"
                            >
                              {procesando[c.id] === 'enviar' ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-send"></i>}
                            </button>
                          )}
                          {/* 'devuelto' también: con error SRI 43/70 la factura ya está en el SRI y regenerar está bloqueado */}
                          {(c.estado === 'enviado' || c.estado === 'recibido' || c.estado === 'devuelto') && (
                            <button
                              className="btn btn-outline-info"
                              onClick={() => ejecutarAccion(c.id, 'consultar', 'Consulta')}
                              disabled={!!procesando[c.id]}
                              title="Consultar autorizacion"
                            >
                              {procesando[c.id] === 'consultar' ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-arrow-repeat"></i>}
                            </button>
                          )}
                          {c.estado === 'autorizado' && (
                            <>
                              <button className="btn btn-outline-danger" onClick={() => descargarRIDE(c.id)} title="Descargar RIDE PDF">
                                <i className="bi bi-file-pdf"></i>
                              </button>
                              <button className="btn btn-outline-success" onClick={() => descargarXML(c.id)} title="Descargar XML">
                                <i className="bi bi-file-code"></i>
                              </button>
                              {c.email_receptor && (
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => enviarEmail(c.id)}
                                  disabled={procesando[c.id] === 'email'}
                                  title={`Enviar a ${c.email_receptor}`}
                                >
                                  {procesando[c.id] === 'email' ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-envelope"></i>}
                                </button>
                              )}
                              {/* Boton Anular (NC) - solo para facturas tipo 01 */}
                              {c.tipo_comprobante === '01' && (
                                <button
                                  className="btn btn-outline-dark"
                                  onClick={() => abrirNCModal(c)}
                                  title="Anular - Generar Nota de Credito"
                                >
                                  <i className="bi bi-x-circle"></i>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginacion */}
            {pagination.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Mostrando {comprobantes.length} de {pagination.total} comprobantes
                </small>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => cargarComprobantes(pagination.page - 1)}>
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const start = Math.max(1, pagination.page - 2);
                      const p = start + i;
                      if (p > pagination.totalPages) return null;
                      return (
                        <li key={p} className={`page-item ${p === pagination.page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => cargarComprobantes(p)}>{p}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => cargarComprobantes(pagination.page + 1)}>
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Notas de Credito */}
      {activeTab === 'notas-credito' && (
        <div className="fade-in">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span><i className="bi bi-file-earmark-minus me-2"></i>Notas de Credito Emitidas</span>
              <button className="btn btn-sm btn-outline-primary" onClick={() => cargarNotasCredito()}>
                <i className="bi bi-arrow-clockwise me-1"></i>Actualizar
              </button>
            </div>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Secuencial</th>
                    <th>Factura Ref.</th>
                    <th>Receptor</th>
                    <th>Motivo</th>
                    <th className="text-end">Total</th>
                    <th className="text-center">Estado</th>
                    <th className="text-center">Devolucion</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <div className="spinner-border text-primary"></div>
                      </td>
                    </tr>
                  ) : notasCredito.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-4 text-muted">
                        <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                        No hay notas de credito
                      </td>
                    </tr>
                  ) : notasCredito.map(nc => (
                    <tr key={nc.id}>
                      <td className="small">{formatFecha(nc.created_at)}</td>
                      <td className="font-monospace fw-bold">
                        {nc.establecimiento}-{nc.punto_emision}-{nc.secuencial}
                      </td>
                      <td className="small">
                        {nc.factura_secuencial ? (
                          <button className="btn btn-link btn-sm p-0 fw-bold font-monospace" onClick={() => verDetalle(nc.comprobante_referencia_id)}>
                            {nc.establecimiento}-{nc.punto_emision}-{nc.factura_secuencial}
                          </button>
                        ) : nc.comprobante_referencia_id ? (
                          <button className="btn btn-link btn-sm p-0" onClick={() => verDetalle(nc.comprobante_referencia_id)}>
                            Ver factura
                          </button>
                        ) : '-'}
                      </td>
                      <td>
                        <div className="small">{nc.razon_social_receptor || 'CONSUMIDOR FINAL'}</div>
                      </td>
                      <td className="small text-truncate" style={{ maxWidth: '200px' }} title={nc.motivo}>
                        {nc.motivo || '-'}
                      </td>
                      {/* FIX: null guard for NC importe_total */}
                      <td className="text-end fw-semibold">{formatCurrency(nc.importe_total || 0)}</td>
                      <td className="text-center">{estadoBadge(nc.estado)}</td>
                      <td className="text-center">
                        {nc.estado === 'autorizado' ? (
                          nc.devoluciones_pendientes > 0 ? (
                            <span className="badge bg-warning text-dark" style={{ cursor: 'pointer' }} onClick={() => abrirDevolucionModal(nc)}>
                              <i className="bi bi-clock-history me-1"></i>Pendiente
                            </span>
                          ) : nc.total_devolver > 0 ? (
                            <span className="badge bg-success">
                              <i className="bi bi-check-circle me-1"></i>Completada
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-secondary" onClick={() => verDetalle(nc.id)} title="Ver">
                            <i className="bi bi-eye"></i>
                          </button>
                          {nc.estado === 'generado' && (
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => ejecutarAccion(nc.id, 'enviar', 'Envio NC')}
                              disabled={!!procesando[nc.id]}
                              title="Firmar y enviar al SRI"
                            >
                              {procesando[nc.id] === 'enviar' ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-send"></i>}
                            </button>
                          )}
                          {(nc.estado === 'enviado' || nc.estado === 'recibido' || nc.estado === 'devuelto') && (
                            <button
                              className="btn btn-outline-info"
                              onClick={() => ejecutarAccion(nc.id, 'consultar', 'Consulta NC')}
                              disabled={!!procesando[nc.id]}
                              title="Consultar autorizacion"
                            >
                              {procesando[nc.id] === 'consultar' ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-arrow-repeat"></i>}
                            </button>
                          )}
                          {nc.estado === 'autorizado' && (
                            <>
                              <button className="btn btn-outline-danger" onClick={() => descargarRIDE(nc.id)} title="RIDE PDF">
                                <i className="bi bi-file-pdf"></i>
                              </button>
                              <button className="btn btn-outline-success" onClick={() => descargarXML(nc.id)} title="XML">
                                <i className="bi bi-file-code"></i>
                              </button>
                              {nc.email_receptor && (
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => enviarEmail(nc.id)}
                                  disabled={procesando[nc.id] === 'email'}
                                  title={`Enviar a ${nc.email_receptor}`}
                                >
                                  {procesando[nc.id] === 'email' ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-envelope"></i>}
                                </button>
                              )}
                              {nc.devoluciones_pendientes > 0 && (
                                <button
                                  className="btn btn-outline-warning"
                                  onClick={() => abrirDevolucionModal(nc)}
                                  title="Gestionar devolucion de dinero"
                                >
                                  <i className="bi bi-cash-stack"></i>
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {paginationNC.totalPages > 1 && (
              <div className="card-footer d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Mostrando {notasCredito.length} de {paginationNC.total}
                </small>
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${paginationNC.page <= 1 ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => cargarNotasCredito(paginationNC.page - 1)}>
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>
                    {Array.from({ length: Math.min(5, paginationNC.totalPages) }, (_, i) => {
                      const start = Math.max(1, paginationNC.page - 2);
                      const p = start + i;
                      if (p > paginationNC.totalPages) return null;
                      return (
                        <li key={p} className={`page-item ${p === paginationNC.page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => cargarNotasCredito(p)}>{p}</button>
                        </li>
                      );
                    })}
                    <li className={`page-item ${paginationNC.page >= paginationNC.totalPages ? 'disabled' : ''}`}>
                      <button className="page-link" onClick={() => cargarNotasCredito(paginationNC.page + 1)}>
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Detalle Comprobante */}
      {showDetalle && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDetalle(null)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-light">
                  <h5 className="modal-title">
                    <i className={`bi ${showDetalle.tipo_comprobante === '04' ? 'bi-file-earmark-minus' : 'bi-file-earmark-check'} me-2`}></i>
                    {tipoLabel(showDetalle.tipo_comprobante)} - Detalle
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowDetalle(null)} />
                </div>
                <div className="modal-body">
                  {/* Estado */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <span className="text-muted me-2">Estado:</span>
                      {estadoBadge(showDetalle.estado)}
                    </div>
                    <div className="text-muted small">
                      {formatFechaHora(showDetalle.created_at)}
                    </div>
                  </div>

                  {/* Clave de Acceso */}
                  {showDetalle.clave_acceso && (
                    <div className="alert alert-light border">
                      <div className="small text-muted mb-1">Clave de Acceso</div>
                      <div className="font-monospace fw-bold" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                        {showDetalle.clave_acceso}
                      </div>
                    </div>
                  )}

                  {/* Numero autorizacion */}
                  {showDetalle.numero_autorizacion && (
                    <div className="alert alert-success border-success bg-success bg-opacity-10">
                      <div className="small text-muted mb-1">Numero de Autorizacion</div>
                      <div className="font-monospace fw-bold" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                        {showDetalle.numero_autorizacion}
                      </div>
                      <div className="small text-muted mt-1">
                        Fecha: {formatFechaHora(showDetalle.fecha_autorizacion)}
                      </div>
                    </div>
                  )}

                  <div className="row g-3">
                    {/* Emisor */}
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header py-2 small fw-bold">
                          <i className="bi bi-building me-1"></i>Emisor
                        </div>
                        <div className="card-body py-2 small">
                          <div><strong>RUC:</strong> {showDetalle.ruc_emisor}</div>
                          <div><strong>Razon Social:</strong> {showDetalle.razon_social_emisor}</div>
                          <div><strong>No. Comprobante:</strong> <span className="fw-bold font-monospace fs-6">{showDetalle.establecimiento}-{showDetalle.punto_emision}-{showDetalle.secuencial}</span></div>
                          <div><strong>Establecimiento:</strong> {showDetalle.establecimiento}</div>
                          <div><strong>Punto Emision:</strong> {showDetalle.punto_emision}</div>
                        </div>
                      </div>
                    </div>
                    {/* Receptor */}
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-header py-2 small fw-bold">
                          <i className="bi bi-person me-1"></i>Receptor
                        </div>
                        <div className="card-body py-2 small">
                          <div><strong>Identificacion:</strong> {showDetalle.identificacion_receptor || '9999999999999'}</div>
                          <div><strong>Razon Social:</strong> {showDetalle.razon_social_receptor || 'CONSUMIDOR FINAL'}</div>
                          {showDetalle.email_receptor && (
                            <div><strong>Email:</strong> {showDetalle.email_receptor}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Montos */}
                  <div className="card mt-3">
                    <div className="card-header py-2 small fw-bold">
                      <i className="bi bi-calculator me-1"></i>Montos
                    </div>
                    <div className="card-body py-2">
                      {/* FIX: null guards for parseFloat on all monetary fields */}
                      <div className="row small">
                        <div className="col-6">
                          <div className="d-flex justify-content-between py-1">
                            <span>Subtotal sin impuestos:</span>
                            <strong>{formatCurrency(showDetalle.subtotal_sin_impuestos || 0)}</strong>
                          </div>
                          {parseFloat(showDetalle.total_descuento || 0) > 0 && (
                            <div className="d-flex justify-content-between py-1 text-danger">
                              <span>Descuento:</span>
                              <strong>-{formatCurrency(showDetalle.total_descuento)}</strong>
                            </div>
                          )}
                          {parseFloat(showDetalle.subtotal_0 || 0) > 0 && (
                            <div className="d-flex justify-content-between py-1">
                              <span>Subtotal 0%:</span>
                              <span>{formatCurrency(showDetalle.subtotal_0)}</span>
                            </div>
                          )}
                        </div>
                        <div className="col-6">
                          {parseFloat(showDetalle.subtotal_iva || 0) > 0 && (
                            <div className="d-flex justify-content-between py-1">
                              <span>Subtotal IVA {showDetalle.iva_porcentaje || 15}%:</span>
                              <span>{formatCurrency(showDetalle.subtotal_iva)}</span>
                            </div>
                          )}
                          <div className="d-flex justify-content-between py-1">
                            <span>IVA {showDetalle.iva_porcentaje || 15}%:</span>
                            <strong>{formatCurrency(showDetalle.valor_iva || 0)}</strong>
                          </div>
                          <hr className="my-1" />
                          <div className="d-flex justify-content-between py-1 fs-6">
                            <strong>TOTAL:</strong>
                            {/* FIX: null guard for importe_total */}
                            <strong className="text-primary">{formatCurrency(showDetalle.importe_total || 0)}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Motivo NC */}
                  {showDetalle.motivo && (
                    <div className="alert alert-info mt-3 small">
                      <i className="bi bi-info-circle me-1"></i>
                      <strong>Motivo:</strong> {showDetalle.motivo}
                    </div>
                  )}

                  {/* Mensajes SRI */}
                  {showDetalle.mensajes_sri && showDetalle.mensajes_sri.length > 0 && (
                    <div className="card mt-3">
                      <div className="card-header py-2 small fw-bold">
                        <i className="bi bi-chat-dots me-1"></i>Mensajes del SRI
                      </div>
                      <div className="card-body py-2">
                        {/* FIX: null safety — guard array */ }
                        {(showDetalle.mensajes_sri || []).map((msg, idx) => (
                          <div key={idx} className={`small p-2 mb-1 rounded ${msg.tipo === 'ERROR' ? 'bg-danger bg-opacity-10' : 'bg-warning bg-opacity-10'}`}>
                            <strong>[{msg.identificador}]</strong> {msg.mensaje}
                            {msg.informacionAdicional && (
                              <div className="text-muted mt-1">{msg.informacionAdicional}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <div className="d-flex gap-2 flex-wrap">
                    {showDetalle.estado === 'generado' && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => ejecutarAccion(showDetalle.id, 'enviar', 'Envio')}
                        disabled={!!procesando[showDetalle.id]}
                      >
                        {procesando[showDetalle.id] === 'enviar' ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Enviando...</>
                        ) : (
                          <><i className="bi bi-send me-1"></i>Firmar y Enviar</>
                        )}
                      </button>
                    )}
                    {(showDetalle.estado === 'enviado' || showDetalle.estado === 'recibido' || showDetalle.estado === 'devuelto') && (
                      <button
                        className="btn btn-info btn-sm"
                        onClick={() => ejecutarAccion(showDetalle.id, 'consultar', 'Consulta')}
                        disabled={!!procesando[showDetalle.id]}
                      >
                        {procesando[showDetalle.id] === 'consultar' ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span>Consultando...</>
                        ) : (
                          <><i className="bi bi-arrow-repeat me-1"></i>Consultar Autorizacion</>
                        )}
                      </button>
                    )}
                    {showDetalle.estado === 'autorizado' && (
                      <>
                        <button className="btn btn-danger btn-sm" onClick={() => descargarRIDE(showDetalle.id)}>
                          <i className="bi bi-file-pdf me-1"></i>RIDE PDF
                        </button>
                        <button className="btn btn-success btn-sm" onClick={() => descargarXML(showDetalle.id)}>
                          <i className="bi bi-file-code me-1"></i>XML Autorizado
                        </button>
                        {showDetalle.email_receptor && (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => enviarEmail(showDetalle.id)}
                            disabled={procesando[showDetalle.id] === 'email'}
                          >
                            {procesando[showDetalle.id] === 'email' ? (
                              <><span className="spinner-border spinner-border-sm me-1"></span>Enviando...</>
                            ) : (
                              <><i className="bi bi-envelope me-1"></i>Enviar a {showDetalle.email_receptor}</>
                            )}
                          </button>
                        )}
                        {/* Boton Anular desde detalle - solo para facturas tipo 01 */}
                        {showDetalle.tipo_comprobante === '01' && (
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() => { setShowDetalle(null); abrirNCModal(showDetalle); }}
                          >
                            <i className="bi bi-x-circle me-1"></i>Anular (Nota de Credito)
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <button className="btn btn-light btn-sm" onClick={() => setShowDetalle(null)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ Modal Generar Nota de Credito ═══ */}
      {showNCModal && ncComprobante && (
        <>
          <div className="modal-backdrop fade show" onClick={() => !ncGenerando && setShowNCModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger bg-opacity-10 border-danger border-opacity-25">
                  <h5 className="modal-title text-danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Anular Factura - Nota de Credito
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => !ncGenerando && setShowNCModal(false)}
                    disabled={ncGenerando}
                  />
                </div>
                <div className="modal-body">
                  {/* Info de la factura a anular */}
                  <div className="alert alert-light border mb-3">
                    <div className="row small">
                      <div className="col-6">
                        <div className="text-muted">Factura</div>
                        <div className="fw-bold font-monospace">
                          {ncComprobante.establecimiento}-{ncComprobante.punto_emision}-{ncComprobante.secuencial}
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-muted">Total</div>
                        <div className="fw-bold text-primary">{formatCurrency(ncComprobante.importe_total)}</div>
                      </div>
                      <div className="col-12 mt-2">
                        <div className="text-muted">Cliente</div>
                        <div className="fw-medium">{ncComprobante.razon_social_receptor || 'CONSUMIDOR FINAL'}</div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-warning small py-2 mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    Se generara una <strong>Nota de Credito</strong> por el valor total de la factura.
                    Este proceso se envia al SRI y <strong>no se puede revertir</strong>.
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Motivo de la anulacion <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Ej: Error en datos de factura, Devolucion de producto, Anulacion por solicitud del cliente..."
                      value={ncMotivo}
                      onChange={(e) => setNcMotivo(e.target.value)}
                      disabled={ncGenerando}
                      maxLength={300}
                      autoFocus
                    />
                    <div className="form-text">{ncMotivo.length}/300 caracteres</div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-light"
                    onClick={() => setShowNCModal(false)}
                    disabled={ncGenerando}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={generarNotaCredito}
                    disabled={ncGenerando || !ncMotivo.trim()}
                  >
                    {ncGenerando ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Generando Nota de Credito...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-x-circle me-1"></i>
                        Generar Nota de Credito
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ Modal Gestionar Devoluciones ═══ */}
      {showDevModal && devNC && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDevModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-warning bg-opacity-10 border-warning border-opacity-25">
                  <h5 className="modal-title">
                    <i className="bi bi-cash-stack me-2 text-warning"></i>
                    Devolucion de Dinero
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowDevModal(false)} />
                </div>
                <div className="modal-body">
                  {/* Info de la NC */}
                  <div className="alert alert-light border mb-3">
                    <div className="row small">
                      <div className="col-4">
                        <div className="text-muted">Nota de Credito</div>
                        <div className="fw-bold font-monospace">
                          {devNC.establecimiento}-{devNC.punto_emision}-{devNC.secuencial}
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="text-muted">Cliente</div>
                        <div className="fw-medium">{devNC.razon_social_receptor || 'CONSUMIDOR FINAL'}</div>
                      </div>
                      <div className="col-4 text-end">
                        <div className="text-muted">Total NC</div>
                        <div className="fw-bold text-danger fs-5">{formatCurrency(devNC.importe_total)}</div>
                      </div>
                    </div>
                  </div>

                  <p className="small text-muted mb-3">
                    <i className="bi bi-info-circle me-1"></i>
                    La Nota de Credito fue autorizada. A continuacion se muestran las devoluciones de dinero al cliente.
                    Las devoluciones en <strong>efectivo</strong> se registran automaticamente como egreso en caja.
                    Las de <strong>tarjeta o transferencia</strong> requieren confirmacion manual una vez realizada la devolucion.
                  </p>

                  {devLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary"></div>
                      <div className="small text-muted mt-2">Cargando devoluciones...</div>
                    </div>
                  ) : devoluciones.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-check-circle fs-1 d-block mb-2 text-success"></i>
                      No hay devoluciones pendientes
                    </div>
                  ) : (
                    <div className="list-group">
                      {devoluciones.map(dev => (
                        <div key={dev.id} className={`list-group-item ${dev.estado === 'pendiente' ? 'list-group-item-warning' : ''}`}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <div className="fw-semibold">
                                <i className={`bi ${dev.estado === 'completada' ? 'bi-check-circle-fill text-success' : 'bi-clock-history text-warning'} me-2`}></i>
                                {metodoLabel(dev.metodo_pago)}
                              </div>
                              <div className="small text-muted mt-1">
                                {dev.estado === 'completada' ? (
                                  <>
                                    Completada el {formatFechaHora(dev.completada_at)}
                                    {dev.completada_por_nombre && <> por <strong>{dev.completada_por_nombre}</strong></>}
                                    {dev.referencia && <> — Ref: <span className="font-monospace">{dev.referencia}</span></>}
                                  </>
                                ) : (
                                  <>
                                    Pendiente de devolucion al cliente
                                    {(dev.metodo_pago.includes('tarjeta') || dev.metodo_pago === 'transferencia') && (
                                      <span className="text-danger fw-medium"> — Requiere devolucion manual</span>
                                    )}
                                  </>
                                )}
                              </div>
                              {dev.procesado_por_nombre && (
                                <small className="text-muted d-block mt-1">
                                  <i className="bi bi-person me-1"></i>Procesado por: {dev.procesado_por_nombre}
                                </small>
                              )}
                              {dev.notas && <div className="small text-muted fst-italic mt-1">{dev.notas}</div>}
                            </div>
                            <div className="text-end">
                              <div className="fw-bold fs-5">{formatCurrency(dev.monto)}</div>
                              <span className={`badge ${dev.estado === 'completada' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {dev.estado === 'completada' ? 'Devuelto' : 'Pendiente'}
                              </span>
                            </div>
                          </div>

                          {/* Formulario para completar devolucion pendiente */}
                          {dev.estado === 'pendiente' && (
                            <div className="mt-3 pt-3 border-top">
                              <div className="row g-2 align-items-end">
                                <div className="col">
                                  <label className="form-label small mb-1">
                                    Referencia (opcional)
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Ej: Transferencia #12345, Voucher de reverso..."
                                    value={devReferencias[dev.id] || ''}
                                    onChange={(e) => setDevReferencias(prev => ({ ...prev, [dev.id]: e.target.value }))}
                                  />
                                </div>
                                <div className="col-auto">
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => completarDevolucion(dev.id)}
                                    disabled={devProcesando[dev.id]}
                                  >
                                    {devProcesando[dev.id] ? (
                                      <><span className="spinner-border spinner-border-sm me-1"></span>Procesando...</>
                                    ) : (
                                      <><i className="bi bi-check-lg me-1"></i>Marcar como Devuelto</>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Resumen */}
                  {/* FIX: null guards for parseFloat in reduce calculations */}
                  {devoluciones.length > 0 && (
                    <div className="card mt-3 bg-light">
                      <div className="card-body py-2">
                        <div className="row small">
                          <div className="col-4 text-center">
                            <div className="text-muted">Total a devolver</div>
                            <div className="fw-bold">{formatCurrency(devoluciones.reduce((s, d) => s + parseFloat(d.monto || 0), 0))}</div>
                          </div>
                          <div className="col-4 text-center">
                            <div className="text-muted">Devuelto</div>
                            <div className="fw-bold text-success">
                              {formatCurrency(devoluciones.filter(d => d.estado === 'completada').reduce((s, d) => s + parseFloat(d.monto || 0), 0))}
                            </div>
                          </div>
                          <div className="col-4 text-center">
                            <div className="text-muted">Pendiente</div>
                            <div className="fw-bold text-warning">
                              {formatCurrency(devoluciones.filter(d => d.estado === 'pendiente').reduce((s, d) => s + parseFloat(d.monto || 0), 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowDevModal(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FacturacionElectronica;
